import React, { useEffect, useMemo, useRef, useState } from 'react'
import { reportContent, type ReportPayload } from '../../api/moderation'

const REASONS: Array<{ value: ReportPayload['reason']; label: string }> = [
  { value: 'spam', label: 'Spam' },
  { value: 'scam', label: 'Scam or phishing' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'adult', label: 'Adult content' },
  { value: 'minor_safety', label: 'Child safety' },
  { value: 'violence', label: 'Violence' },
  { value: 'other', label: 'Other' },
]

export default function ReportModal({
  open,
  onClose,
  contentType,
  contentId,
  title,
}: {
  open: boolean
  onClose: () => void
  contentType: ReportPayload['content_type']
  contentId: string
  title?: string
}) {
  const [reason, setReason] = useState<ReportPayload['reason']>('spam')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; message: string } | null>(null)
  const closeTimerRef = useRef<number | null>(null)

  const reasonOptions = useMemo(() => REASONS, [])

  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(null), 2400)
    return () => clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  async function submit() {
    if (submitting) return
    setSubmitting(true)
    setToast(null)
    try {
      await reportContent({
        content_type: contentType,
        content_id: contentId,
        reason,
        note: note.trim() || undefined,
      })
      setToast({ tone: 'success', message: 'Report submitted. Thanks for helping keep Pixeloop safe.' })
      setNote('')
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = window.setTimeout(() => {
        closeTimerRef.current = null
        onClose()
      }, 1200)
    } catch (e: any) {
      const status = e?.response?.status
      const msg = status === 429
        ? "You've hit the daily report limit. Try again tomorrow."
        : e?.response?.data?.detail || e?.message || 'Failed to report.'
      setToast({ tone: 'error', message: msg })
    } finally {
      setSubmitting(false)
    }
  }

  function close() {
    if (submitting) return
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setToast(null)
    onClose()
  }

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={close} />
      <div className="absolute inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center">
        <div className="md:w-[520px] w-full bg-neutral-950 border-t md:border border-white/10 rounded-t-2xl md:rounded-2xl p-4">
          <div className="w-10 h-1 rounded bg-white/20 mx-auto mb-2 md:hidden" />
          <div className="flex items-center justify-between mb-2">
            <div className="text-white font-extrabold text-lg">Report</div>
            <button onClick={close} className="px-2 py-1 rounded-md text-white/90 hover:bg-white/10">X</button>
          </div>
          {title && <div className="text-sm text-white/70 mb-3">{title}</div>}
          <label className="block mb-3">
            <div className="text-white/70 text-sm mb-1">Reason</div>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as ReportPayload['reason'])}
              className="w-full rounded-lg bg-black border border-white/20 px-3 py-2 text-white"
            >
              {reasonOptions.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-black text-white">
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block mb-3">
            <div className="text-white/70 text-sm mb-1">Note (optional)</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-lg bg-black border border-white/20 px-3 py-2 text-white min-h-[90px]"
              placeholder="Tell us what happened."
            />
          </label>
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={close} disabled={submitting} className="px-4 py-2 rounded-lg border border-white/15 text-white/90 hover:bg-white/10 disabled:opacity-60">X</button>
            <button onClick={submit} disabled={submitting} className="px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold disabled:opacity-60">
              {submitting ? 'Reporting...' : 'Submit report'}
            </button>
          </div>
        </div>
      </div>
      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-[60]">
          <div
            className={`px-4 py-2 rounded-full border text-sm ${
              toast.tone === 'success'
                ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-100'
                : 'border-rose-400/40 bg-rose-500/15 text-rose-100'
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}

